<template>
  <div
    :id="props.id"
    :class="bem('')"
  >
    <div :class="bem('close-button')">
      <VisionIconButton
        icon="cross"
        @click="handleClose"
      />
    </div>
    <Swiper
      v-bind="swiperOptions"
      :class="bem('swiper')"
    >
      <SwiperSlide
        v-for="item in props.items"
        :key="item.id"
        :class="bem('item')"
      >
        <div :class="bem('media-container')">
          <MediaItem
            :item="item.media"
            aspect-ratio="auto"
          />
        </div>
        <div :class="bem('content')">
          <VisionHeading
            tag="h4"
            size="h2"
            :class="bem('title')"
            >{{ item.title }}</VisionHeading
          >
          <p :class="bem('description')">{{ item.description }}</p>
        </div>
      </SwiperSlide>
    </Swiper>
    <div :class="bem('controls')">
      <CarouselArrow
        arrow="left"
        background-colour="dark-grey"
      />
      <div :class="bem('subtitle-pagination')" />
      <CarouselArrow
        arrow="right"
        background-colour="dark-grey"
      />
    </div>
    <div
      :class="bem('backdrop')"
      @click="handleClose"
    />
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination } from 'swiper/modules'
const bem = useBEM('InteractiveListOverlay')
const pageStore = usePageStore()

interface InteractiveListOverlayProps {
  items: {
    id: string
    title: string
    subtitle: string
    description: string
    media: any
  }[]
  id: string
  activeIndex: number
}

const props = defineProps<InteractiveListOverlayProps>()

const swiperOptions = ref({
  modules: [Navigation, Pagination],
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 300,
  centeredSlides: true,
  loop: true,
  slideToClickedSlide: true,
  initialSlide: props.activeIndex,
  navigation: {
    nextEl: `#${props.id} [data-carousel-arrow="right"]`,
    prevEl: `#${props.id} [data-carousel-arrow="left"]`,
  },
  pagination: {
    clickable: true,
    el: `#${props.id} .InteractiveListOverlay__subtitle-pagination`,
    bulletClass: 'InteractiveListOverlay__pagination-item',
    bulletActiveClass: 'InteractiveListOverlay__pagination-item--is-active',
    renderBullet: function (index: any, className: any) {
      const subtitle = props.items[index]?.subtitle || ''
      return `<span class="${className}">${subtitle}</span>`
    },
  },
  breakpoints: {
    1000: {
      slidesPerView: 1.5,
      spaceBetween: 40,
    },
    1440: {
      slidesPerView: 1.75,
      spaceBetween: 56,
    },
    1660: {
      slidesPerView: 2,
      spaceBetween: 80,
    },
  },
})

const emits = defineEmits(['close'])

const handleClose = () => {
  emits('close')
}

onMounted(() => {
  pageStore.toggleBlockBodyScroll(true)
})

onUnmounted(() => {
  pageStore.toggleBlockBodyScroll(false)
})
</script>

<style lang="postcss">
.InteractiveListOverlay {
  @apply fixed top-0 left-0 right-0 bottom-0 z-[9999] h-screen max-h-screen max-w-[100vw];
}

.InteractiveListOverlay__backdrop {
  @apply absolute cursor-pointer z-0 inset-0 backdrop-blur-dark;
}

.InteractiveListOverlay__close-button {
  @apply absolute z-[2] top-5 right-5;

  .VisionIconButton {
    @apply p-3;
    --vision-icon-size: 16px;
    --vision-icon-button-bg-colour: theme('colors.grey.800');
  }
}

.InteractiveListOverlay__swiper {
  @apply h-full;
}

.InteractiveListOverlay__item {
  @apply p-[var(--outer-gutter)] h-full text-white
  flex justify-center flex-col gap-10;

  @screen lg {
    @apply p-0;
  }
}

.InteractiveListOverlay__media-container {
  @apply relative aspect-[5/4] w-full max-h-full rounded-lg overflow-clip;

  @screen lg {
    @apply cursor-pointer;

    &::before {
      @apply content-[''] absolute inset-0 bg-black z-[2] opacity-50;
      transition: opacity 0.16s linear;
    }
  }
}

.InteractiveListOverlay__item.swiper-slide-active {
  .InteractiveListOverlay__media-container::before {
    @apply opacity-0;
  }
}

.InteractiveListOverlay__content {
  @apply md:hidden;
}

.InteractiveListOverlay__title {
  @apply text-2xl mb-5;
}

.InteractiveListOverlay__controls {
  --vision-icon-size: 12px;
  @apply absolute z-[1] bottom-0 left-0 right-0
  p-[var(--outer-gutter)] lg:p-5 flex justify-between items-center;

  .VisionIconButton {
    @apply p-2.5 bg-grey-800;
  }
}

.InteractiveListOverlay__subtitle-pagination {
  @apply w-full flex items-center justify-center gap-8;
}

.InteractiveListOverlay__pagination-item {
  @apply absolute md:relative opacity-0 md:opacity-30 text-grey-200 font-fragment-mono text-xxs
  cursor-pointer;
  transition: opacity 0.16s linear;

  &--is-active {
    @apply opacity-100 delay-[0.16s];
  }
}
</style>
